import { useTranslation } from 'react-i18next'
import { StyleSheet, useWindowDimensions, View, ViewProps } from 'react-native'
import { Button, Color, Font, Radius, Spacing, useKeyboardShortcut } from 'zens-storybook'
import { Suspense } from 'react'
import { useNumberParams, Link } from 'zens-router'
import { useCompany } from 'zens-store'
import {
  InsightsHead,
  InsightsHeadItem,
  InsightsLiveFloorplan,
  InsightsHeader,
  LayoutHeader,
  MenuMore,
} from '@dms/component'
import InsightsLegendLive from '@dms/component/insights/legendlive'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
  },
  block: {
    marginTop: Spacing.M,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
  top: {
    flexDirection: 'row',
    flex: 1,
    zIndex: 100,
  },
  head: {
    flex: 1,
  },
  legend: {
    position: 'absolute',
    left: 50 + Spacing.S,
    bottom: Spacing.S,
  },
  more: {
    backgroundColor: Color.simplyWhite,
    borderBottomColor: Color.calmGrey,
    borderBottomWidth: 1,
  },
  moreTitle: {
    fontFamily: Font.medium,
  },
})

interface Props extends ViewProps {}

const InsightsLive = (props: Props) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  const [{ companyId, buildingId, floorId, rotation = 0 }, setNumberParams] = useNumberParams()
  const { height } = useWindowDimensions()
  const company = useCompany({ companyId })

  useKeyboardShortcut(['ctrl', 'r'], () => more('rotate-left'))

  function more(key: string) {
    if (key === 'rotate-left') setNumberParams({ rotation: ((rotation || 0) + 270) % 360 })
    else setNumberParams({ rotation: ((rotation || 0) + 90) % 360 })
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <InsightsHeader
          companyId={companyId}
          buildingId={buildingId}
          title={t('Live status page')}
          breadcrumbItems={[{ title: t('Live') }]}
        />
      </Suspense>
      <View style={styles.content}>
        <View style={styles.top}>
          <Suspense
            fallback={
              <>
                <InsightsHead.Skeleton style={styles.head} />
              </>
            }
          >
            <InsightsHead
              style={styles.head}
              companyId={companyId}
              buildingId={buildingId}
              items={InsightsHeadItem.Building | InsightsHeadItem.Floor}
              floorExtraItems={[]}
              href={(buildingId, floorId) => ({
                pathname: `/companies/${companyId}/insights/${buildingId}/floors/${floorId}/live`,
                params: { rotation },
              })}
            />
            <Link
              href={{
                pathname: `/companies/${companyId}/insights/${buildingId}/floors/${floorId}/live/kiosk`,
                params: { rotation },
              }}
              asChild
            >
              <Button title={t('View in kiosk mode')} left="eyeOpen" light testID="InsightsLive.Kiosk" />
            </Link>
            <MenuMore
              items={[
                {
                  key: 'title',
                  title: t('Floorplan options'),
                  disabled: true,
                  style: styles.more,
                  textStyle: styles.moreTitle,
                },
                {
                  key: 'rotate-left',
                  icon: 'gear',
                  title: t('Rotate 90° left'),
                  testID: 'InsightsLive.RotateLeft',
                },
                {
                  key: 'rotate-right',
                  icon: 'gear',
                  title: t('Rotate 90° right'),
                  testID: 'InsightsLive.RotateRight',
                },
              ]}
              onSelect={(key) => more(key as string)}
              testID="InsightsLive.More"
            />
          </Suspense>
        </View>
        <View style={styles.block}>
          <Suspense fallback={<InsightsLiveFloorplan.Skeleton style={{ height: height - 216 - Spacing.M }} />}>
            <InsightsLiveFloorplan
              companyId={companyId}
              buildingId={buildingId}
              floorId={floorId}
              colorScheme={company.data!.colorScheme}
              style={{ height: height - 216 - Spacing.M }}
              overlay={<InsightsLegendLive style={styles.legend} colorScheme={company.data!.colorScheme} />}
              rotation={rotation as 0 | 90 | 180 | 270}
            />
          </Suspense>
        </View>
      </View>
    </View>
  )
}

export default InsightsLive
